import api from './client';

export interface Channel {
  id: string;
  publicId: string;
  name: string;
  slug: string;
  description: string;
  type: 'discussion' | 'qa' | 'library' | 'events' | 'gallery' | 'intros' | 'polls';
  visibility: 'public' | 'restricted' | 'hierarchy';
  icon: string;
  postCount: number;
  memberCount: number;
  isArchived: boolean;
  accessStatus?: 'approved' | 'pending' | 'rejected' | 'banned' | null;
  /** Explicit channel_members role — moderator/admin can post even when settings.readOnly=true */
  accessRole?: 'member' | 'moderator' | 'admin' | null;
  /** Channel-level settings blob — settings.readOnly locks the channel for non-mods */
  settings?: { readOnly?: boolean; [key: string]: unknown } | null;
  /** Protect all media in this channel (blur overlay on web, future: OS-level block Spec 182) */
  protectMedia?: boolean;
  /** Posts created after last_visited_at — null/undefined = never visited */
  unreadCount?: number;
}

export interface PostEmbed {
  id: number;
  publicId: string;
  postId: number;
  embedType: 'protocol' | 'task' | 'contract' | 'limits';
  sourceId: number | null;
  embedData: Record<string, unknown>;
  importCount: number;
  createdAt: string;
}

export interface PostMedia {
  id: string;
  publicId: string;
  fileUrl: string;
  fileType: string;
  fileSize?: number;
  width?: number;
  height?: number;
  thumbnailUrl?: string;
}

export interface ChannelPost {
  id: string;
  // NOT `authorId: string` — the backend strips the numeric author_id
  // (stripInternalIds' INTERNAL_ID_FIELDS set includes 'author_id') and
  // never re-adds an aliased field named authorId. A prior version of this
  // interface declared `authorId: string` anyway; it was dead (grep-
  // confirmed zero readers anywhere in the frontend) and would have
  // resolved to `undefined` at runtime for the first real consumer.
  // authorPublicId is what the backend actually sends
  // (getChannelPosts/createPost both select `u.public_id AS author_public_id`,
  // camelCased by the response middleware) — use this for any identity
  // check or API call that needs the post author's id (Spec 277 Track C's
  // moderator actions target this field).
  authorPublicId: string;
  authorName: string;
  authorAvatar?: string;
  title?: string;
  content: string;
  likeCount: number;
  commentCount: number;
  isLiked?: boolean;
  isPinned?: boolean;
  /** Spec 277 Track C: locked posts cannot receive new comments (enforced server-side) */
  isLocked?: boolean;
  createdAt: string;
  // Q&A fields
  acceptedAnswerId?: number | null;
  acceptedAnswerContent?: string | null;
  acceptedAnswerAuthor?: string | null;
  // Library fields
  libraryCategory?: string | null;
  saveCount?: number;
  isSaved?: boolean;
  // Embed fields (Spec 132)
  embeds?: PostEmbed[];
  // Media fields (Spec 171)
  media?: PostMedia[];
  // Protect media on this specific post
  protectMedia?: boolean;
}

export interface ChannelComment {
  id: number;
  // Spec 299: stable UUID identifier, used to build the
  // `data-testid="comment-${publicId}"` DOM anchor a deep-linked mention
  // notification scrolls to + highlights. Always present — the backend
  // (channelController.getChannelPostComments/addChannelPostComment) always
  // returns it, same guarantee authorPublicId documents below.
  publicId: string;
  postId: number;
  parentId: number | null;
  // Numeric internal author FK from community_post_comments.author_id.
  // NEVER compare this to user.id (which is a UUID string) — the comparison
  // is always false. Use authorPublicId for identity checks. See the
  // ApiPost / mapApiPost split in components/Community/Feed/types.ts for
  // the canonical pattern.
  authorId: number;
  // UUID author identity from users.public_id. The backend
  // (channelController.getChannelPostComments + addChannelPostComment)
  // always returns this field — required, not optional.
  authorPublicId: string;
  authorName: string;
  authorAvatarUrl?: string;
  content: string;
  createdAt: string;
}

export interface PresenceMember {
  userId: number;
  publicId: string;
  displayName: string;
  trustLevel: number;
  trustBadge: string;
}

export interface ChannelPresenceResponse {
  online: PresenceMember[];
  totalOnline: number;
}

export interface PostableChannel {
  id: number;
  publicId: string;
  name: string;
  slug: string;
  visibility: string;
  type: string;
  memberCount: number;
}

export interface ChannelMember {
  userId: string;
  publicId: string;
  displayName: string;
  role: 'member' | 'moderator';
  status: string;
  joinedAt?: string;
}

// ============================================================================
// Spec 277 Track C: Channel Moderator Admin Toolkit
// ============================================================================

export type ChannelModerationActionType =
  | 'hide' | 'unhide' | 'pin' | 'unpin' | 'lock' | 'unlock'
  | 'warn' | 'mute' | 'unmute' | 'ban' | 'unban';

/** Full log entry — MODERATOR-ONLY shape (includes moderator + target identity). */
export interface ChannelModerationLogEntry {
  id: string;
  // channelId/moderatorId/targetUserId/targetPostId are internal numeric IDs
  // and are stripped server-side (publicIdMapper.ts's INTERNAL_ID_FIELDS) —
  // use the *PublicId/*Name/*Title fields below instead. Do not re-add these
  // without also removing them from INTERNAL_ID_FIELDS.
  moderatorName: string | null; // NULL when the moderator deleted their account (0zzz-el)
  moderatorPublicId: string | null;
  targetUserName: string | null;
  targetUserPublicId: string | null;
  targetPostTitle: string | null;
  actionType: ChannelModerationActionType;
  reason: string | null;
  expiresAt: string | null;
  createdAt: string;
}

/**
 * The CEO visibility-split, privacy-scoped shape — a member's OWN notices
 * only. Deliberately has no channelId/targetUserId/targetPostId fields; the
 * backend query already filters to the caller's own rows, so there is
 * nothing here to compare against another user's id.
 */
export interface ChannelModerationNotice {
  id: string;
  actionType: ChannelModerationActionType;
  reason: string | null;
  expiresAt: string | null;
  createdAt: string;
  moderatorName: string | null; // NULL when the moderator deleted their account (0zzz-el)
}

// Real wire shape, confirmed against channelService.getAccessRequests /
// channelController.getAccessRequests directly (bug-registry.md 0zzz-ar):
// `{ user_public_id, display_name, request_message, requested_at }`,
// camelCased on the way out. `cm.id` (channel_members.id) is stripped
// server-side by stripInternalIdsFromRows as an internal id — never present.
// There is no `email` field — getAccessRequests never selects one.
export interface PendingRequest {
  userPublicId: string;
  displayName: string;
  requestMessage: string | null;
  requestedAt: string;
}

// Spec 283: Channel Self-Identification Tags. Platform-curated catalog —
// moderators enable/disable per channel, members self-declare values. All
// optional, no "declined" state, channel-members-only visibility. See
// docs/specs/283-channel-self-identification-tags.md.
export interface ChannelTagCatalogValue {
  value: string;
  label: string;
}

export interface ChannelTagCategory {
  id: number;
  label: string;
  values: ChannelTagCatalogValue[];
  multiSelect: boolean;
  status: 'approved' | 'pending' | 'rejected';
  requestedByChannelId: number | null;
  requestedByUserId: number | null;
  approvedByUserId: number | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ChannelTagCategoryRequest extends ChannelTagCategory {
  requestedByChannelName: string | null;
  requestedByUsername: string | null;
}

// Categories currently enabled for a channel + their curated values — for
// the member's own tag-setting control.
export interface EnabledChannelTagCategory {
  channelTagCategoryId: number;
  catalogCategoryId: number;
  label: string;
  values: ChannelTagCatalogValue[];
  multiSelect: boolean;
}

// The caller's own tag values in a channel, keyed by catalog category id.
export type OwnChannelTags = Record<number, string[]>;

// Bulk read for member-list / post-author byline rendering — ONE channel
// per call (Open Question 4 resolution: dedicated endpoint, not embedded
// into the posts query). Keyed by userPublicId (UUID), matching
// ChannelPost.authorPublicId — the backend never exposes a numeric authorId
// to the frontend for posts, so this bulk read has to use the same space.
export interface ChannelMemberTagValue {
  userPublicId: string;
  catalogCategoryId: number;
  label: string;
  value: string;
}

export interface ChannelListResponse {
  public: Channel[];
  restricted: Channel[];
  hierarchy: Channel[];
}

// ─── Spec 276: Channel Challenges ───────────────────────────────────────────

export type ChannelChallengeType = 'general' | 'task_completion' | 'streak' | 'points' | 'custom';
export type ChannelChallengeGoalType = 'count' | 'duration' | 'points' | 'custom';
export type ChannelChallengeStatusFilter = 'active' | 'upcoming' | 'ended' | 'all';

export interface ChannelChallenge {
  id: number;
  publicId: string;
  // NOTE: no channelId/createdBy fields — the backend strips these as
  // internal numeric IDs (publicIdMapper's INTERNAL_ID_FIELDS) before the
  // response leaves the server. The frontend never needs them: channelId is
  // already known from the page's own route param, and createdBy has no
  // frontend consumer (ownership/moderator checks are server-side only, per
  // this repo's "backend enforces every role gate the frontend hides" rule).
  title: string;
  description: string | null;
  challengeType: ChannelChallengeType;
  goalType: ChannelChallengeGoalType;
  goalValue: number | null;
  startDate: string;
  endDate: string;
  rewardBadge: string | null;
  rewardPoints: number;
  maxParticipants: number | null;
  isActive: boolean;
  createdAt: string;
  // Present on detail responses, and on list rows where the backend includes them.
  participantCount?: number;
  userJoined?: boolean;
  userProgress?: number;
}

export interface ChannelChallengeParticipant {
  id: number;
  // NOTE: no challengeId/userId fields — the backend strips both as internal
  // numeric IDs (publicIdMapper's INTERNAL_ID_FIELDS). challengeId is already
  // known from the page's own route param. Comparing a numeric userId
  // against the frontend's UUID user.id would silently always be false
  // anyway (the ID-space footgun this repo's CLAUDE.md documents) — use
  // isCurrentUser below instead, computed server-side.
  progress: number;
  completedAt: string | null;
  rank: number | null;
  joinedAt: string;
  // Display-name-privacy-gated (getDisplayNameSql) — never a raw username.
  userName?: string;
  // Computed server-side (cp.user_id === req.user.id, both numeric) — never
  // derive this client-side.
  isCurrentUser?: boolean;
}

export interface ChannelChallengeListResponse {
  challenges: ChannelChallenge[];
  total: number;
  page: number;
  limit: number;
}

export interface ChannelChallengeLeaderboardResponse {
  leaderboard: ChannelChallengeParticipant[];
}

export interface CreateChannelChallengeInput {
  title: string;
  description?: string;
  challengeType: ChannelChallengeType;
  goalType: ChannelChallengeGoalType;
  goalValue?: number;
  startDate: string;
  endDate: string;
  rewardBadge?: string;
  rewardPoints?: number;
  maxParticipants?: number;
}

export interface ChannelPostsResponse {
  posts: ChannelPost[];
  channelType?: string;
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  /**
   * Spec 299 Phase 2 — this user's channel_members.last_visited_at cursor as
   * it stood BEFORE this request (read-only; getChannelPosts never writes
   * it). Drives ChannelFeed's "new messages" divider: null means "never
   * visited" (fresh member, or a public-channel viewer with no member row).
   * ISO string (JSON date), not a Date — matches every other timestamp this
   * API surface returns.
   */
  previousLastVisitedAt: string | null;
}

// Spec 299 Phase 1 — response shape for GET /channels/:channelId/posts/around/:postId,
// per docs/mockups/299-channels-post-permalink/README.md's "Proposed API shape".
export interface ChannelPostsAroundResponse {
  posts: ChannelPost[];
  targetPostId: string;
  hasNewer: boolean;
  hasOlder: boolean;
  newestLoadedPostId: string | null;
  oldestLoadedPostId: string | null;
  /** Echoes the requested comment id back only if it still resolves — null if deleted/invalid/absent. */
  targetCommentId: string | null;
  /** null when no commentId was requested at all (reply landing, not a mention). */
  targetCommentFound: boolean | null;
  channelType: string;
}

// ─── Spec 277 Track C: Channel Polls ───────────────────────────────────────
// Standalone entity (channel_polls/channel_poll_votes) — no post_id, unlike
// Communities' community_polls. Single-select, single-vote-per-user (v1
// scope, no vote-changing). Tallies + the viewer's own vote are always
// visible; there is no getVoters()-style endpoint (privacy boundary — see
// channelPollService.ts's getResults() docstring).

export interface ChannelPollOptionResult {
  id: number;
  text: string;
  votes: number;
  percentage: number;
}

export interface ChannelPoll {
  id: string; // publicId, surfaced as `id` (backend strips internal numeric ids)
  question: string;
  options: Array<{ id: number; text: string }>;
  closesAt: string | null;
  createdAt: string;
  // Display-name-privacy-gated (getDisplayNameSql) — never a raw username.
  authorName: string;
  totalVotes: number;
  // The requester's own voted option id, or null if they haven't voted yet.
  // Never a list of other voters — see the privacy-boundary note above.
  userVotedOptionId: number | null;
  results: ChannelPollOptionResult[];
}

export interface ChannelPollListResponse {
  polls: ChannelPoll[];
  total: number;
}

export interface CreateChannelPollInput {
  question: string;
  options: string[]; // 2-10 entries; sanitized server-side
  closesAt?: string | null;
}

// =============================================================================
// Spec 275: Channel Events
//
// Field names below match the ACTUAL backend contract (communityEventService.ts's
// `CommunityEvent`, returned via `SELECT e.*` + camelCase middleware) — NOT the
// (incorrect) field names used by the legacy Communities Engagement/EventCard.tsx
// (attendeeCount/userIsAttending/isOnline/meetingUrl/hostId did not exist on the
// wire; the real fields are rsvpCount/userRsvp/isVirtual/virtualLink/createdBy).
// That component was removed in Spec 277 Track A along with the rest of legacy
// Communities — the bug never propagated into CreateEventModal.tsx/EventWorkshops.tsx
// (relocated to components/channels/ in the same pass), which already used the
// correct field names.
// =============================================================================

export interface ChannelEvent {
  id: number;
  publicId: string;
  communityId: number;
  channelId: number | null;
  title: string;
  description: string | null;
  eventType: string;
  startTime: string;
  endTime: string | null;
  timezone: string;
  location: string | null;
  isVirtual: boolean;
  virtualLink: string | null;
  maxAttendees: number | null;
  rsvpCount: number;
  coverImageUrl: string | null;
  isCancelled: boolean;
  createdBy: number;
  createdAt: string;
  /** Current user's RSVP status, or null/undefined if they haven't RSVP'd */
  userRsvp?: 'going' | 'maybe' | 'not_going' | null;
  /** Joined via COALESCE(display_name, username) in getEvent()/listChannelEvents() */
  hostName: string;
}

export interface ChannelEventListResponse {
  events: ChannelEvent[];
  total: number;
  page: number;
  limit: number;
}

export const channelAPI = {
  list: () => api.get<{ success: boolean; data: ChannelListResponse }>('/channels'),

  get: (channelId: string) => api.get<{ success: boolean; data: Channel }>(`/channels/${channelId}`),

  getPosts: (channelId: string, params?: { page?: number; pageSize?: number; sort?: string; category?: string }) =>
    api.get<{ success: boolean; data: ChannelPostsResponse }>(`/channels/${channelId}/posts`, { params }),

  // Spec 299 Phase 1: bounded window of posts centered on a target post —
  // used only when a deep-linked target isn't on the normal page-1 load.
  getPostsAround: (channelId: string, postId: string, params?: { before?: number; after?: number; commentId?: string }) =>
    api.get<{ success: boolean; data: ChannelPostsAroundResponse }>(`/channels/${channelId}/posts/around/${postId}`, { params }),

  createPost: (channelId: string, data: { title?: string; content: string; libraryCategory?: string; protectMedia?: boolean; mediaKeys?: Array<{ fileKey: string; fileUrl: string; fileType: string; fileSize?: number }> }) =>
    api.post(`/channels/${channelId}/posts`, data),

  markVisited: (channelId: string) =>
    api.post(`/channels/${channelId}/visit`),

  // Spec 171: Channel Media Posts
  getUploadUrl: (channelId: string, fileName: string, fileType: string, fileSize?: number) =>
    api.post<{ success: boolean; data: { uploadUrl: string; uploadKey: string; sessionId: string } }>(
      `/channels/${channelId}/posts/upload`,
      { fileName, mimeType: fileType, fileSize: fileSize || 0 }
    ),

  deletePostMedia: (channelId: string, postId: string, mediaId: string) =>
    api.delete(`/channels/${channelId}/posts/${postId}/media/${mediaId}`),

  // commentId is the comment's public_id (UUID) -- the backend resolves it
  // via resolveCommentId(), UUID-only like every other identifier in that
  // file (2026-08-14 Channels red-team fix); this type was never updated to
  // match when that landed. Currently unreachable (no component calls this
  // function yet, confirmed via repo-wide grep), but the next "Accept
  // Answer" UI wiring must not pass a numeric id here.
  acceptAnswer: (channelId: string, postId: string, commentId: string) =>
    api.put(`/channels/${channelId}/posts/${postId}/accept-answer`, { commentId }),

  unacceptAnswer: (channelId: string, postId: string) =>
    api.delete(`/channels/${channelId}/posts/${postId}/accept-answer`),

  toggleSave: (channelId: string, postId: string) =>
    api.post(`/channels/${channelId}/posts/${postId}/save`),

  toggleLike: (channelId: string, postId: string) =>
    api.post<{ success: boolean; data: { liked: boolean; likeCount: number } }>(`/channels/${channelId}/posts/${postId}/like`),

  getComments: (channelId: string, postId: string) =>
    api.get<{ success: boolean; data: { comments: ChannelComment[]; total: number } }>(`/channels/${channelId}/posts/${postId}/comments`),

  addComment: (channelId: string, postId: string, content: string) =>
    api.post<{ success: boolean; data: ChannelComment }>(`/channels/${channelId}/posts/${postId}/comments`, { content }),

  requestAccess: (channelId: string, message?: string) =>
    api.post(`/channels/${channelId}/request-access`, { message }),

  // Spec 288 Phase 3: self-service leave (member-facing, distinct from the
  // moderator-only remove-member endpoint)
  leaveChannel: (channelId: string) =>
    api.post(`/channels/${channelId}/leave`),

  // Spec 132: Helm Object Embeds
  createEmbed: (channelId: string, postId: string, data: {
    embedType: string;
    sourceId?: number;
    embedData: Record<string, unknown>;
  }) => api.post(`/channels/${channelId}/posts/${postId}/embed`, data),

  importEmbed: (embedId: string, organizationId: string) =>
    api.post(`/channels/embeds/${embedId}/import`, { organizationId }),

  // Spec 133: Presence
  getPresence: (channelId: string) =>
    api.get<{ success: boolean; data: ChannelPresenceResponse }>(`/channels/${channelId}/presence`),

  // Gap 3: Share to Channel
  getPostableChannels: () =>
    api.get<{ success: boolean; data: PostableChannel[] }>('/channels/postable'),

  shareToChannel: (channelId: string, data: {
    entityType: 'protocol' | 'task' | 'contract';
    entityId: number | string;
    message?: string;
    includeHierarchyName?: boolean;
  }) => api.post<{ success: boolean; data: { postId: number; embedId: number } }>(
    `/channels/${channelId}/share`,
    data
  ),

  // Spec 140: Channel Management (super_admin)
  createChannel: (data: {
    name: string;
    description?: string;
    type: string;
    visibility: string;
    icon?: string;
    protectMedia?: boolean;
    memberIds?: string[];
    members?: Array<{ id: string; role: string }>;
  }) => api.post<{ success: boolean; data: { channel: Channel } }>('/channels', data),

  updateChannel: (channelId: string, data: {
    name?: string;
    description?: string;
    type?: string;
    visibility?: string;
    icon?: string;
    protectMedia?: boolean;
  }) => api.put<{ success: boolean; data: { channel: Channel } }>(`/channels/${channelId}`, data),

  archiveChannel: (channelId: string) =>
    api.delete(`/channels/${channelId}`),

  addChannelMembers: (channelId: string, userIds: string[]) =>
    api.post(`/channels/${channelId}/members`, { userIds }),

  removeChannelMember: (channelId: string, userId: string) =>
    api.delete(`/channels/${channelId}/members/${userId}`),

  getChannelMembers: (channelId: string) =>
    api.get<{ success: boolean; data: { members: Array<{ userId: string; displayName: string; status: string }> } }>(
      `/channels/${channelId}/members`
    ),

  searchUsersForChannel: (query: string) =>
    api.get<{ success: boolean; data: Array<{ publicId: string; displayName: string; email: string }> }>(
      '/channels/search-users',
      { params: { q: query } }
    ),

  // Spec 170: Channel Member Management
  getMembers: (channelId: string) =>
    api.get<{ success: boolean; data: { members: ChannelMember[] } }>(
      `/channels/${channelId}/members`
    ),

  addMember: (channelId: string, userId: string, role: 'member' | 'moderator' = 'member') =>
    api.post(`/channels/${channelId}/members`, { userIds: [userId], role }),

  removeMember: (channelId: string, userId: string) =>
    api.delete(`/channels/${channelId}/members/${userId}`),

  updateMemberRole: (channelId: string, userId: string, role: 'member' | 'moderator') =>
    api.put(`/channels/${channelId}/members/${userId}/role`, { role }),

  getPendingRequests: (channelId: string) =>
    api.get<{ success: boolean; data: { requests: PendingRequest[] } }>(
      `/channels/${channelId}/access-requests`
    ),

  approveAccess: (channelId: string, userId: string) =>
    api.put(`/channels/${channelId}/members/${userId}/approve`),

  rejectAccess: (channelId: string, userId: string) =>
    api.put(`/channels/${channelId}/members/${userId}/reject`),

  // Spec 276: Channel Challenges
  // Every endpoint below actually returns { success: true, data: {...} },
  // same as the rest of this file (verified directly against
  // channelChallengeController.ts) — typed here as the unwrapped shape only
  // because every caller pipes through unwrapResponse(), which transparently
  // unwraps the real `{ data: ... }` envelope. Don't skip unwrapResponse() on
  // these call sites — the type below does not reflect the actual wire shape.
  listChallenges: (channelId: string, params?: { status?: ChannelChallengeStatusFilter; page?: number; limit?: number }) =>
    api.get<ChannelChallengeListResponse>(`/channels/${channelId}/challenges`, { params }),

  getChallenge: (channelId: string, challengeId: string) =>
    api.get<{ success: boolean; data: ChannelChallenge }>(`/channels/${channelId}/challenges/${challengeId}`),

  createChallenge: (channelId: string, data: CreateChannelChallengeInput) =>
    api.post<{ success: boolean; data: ChannelChallenge }>(`/channels/${channelId}/challenges`, data),

  updateChallenge: (channelId: string, challengeId: string, data: Partial<CreateChannelChallengeInput>) =>
    api.put<{ success: boolean; data: ChannelChallenge }>(`/channels/${channelId}/challenges/${challengeId}`, data),

  deleteChallenge: (channelId: string, challengeId: string) =>
    api.delete(`/channels/${channelId}/challenges/${challengeId}`),

  joinChallenge: (channelId: string, challengeId: string) =>
    api.post<{ success: boolean; data: ChannelChallengeParticipant }>(`/channels/${channelId}/challenges/${challengeId}/join`),

  leaveChallenge: (channelId: string, challengeId: string) =>
    api.post(`/channels/${channelId}/challenges/${challengeId}/leave`),

  updateChallengeProgress: (channelId: string, challengeId: string, progress: number) =>
    api.put<{ success: boolean; data: ChannelChallengeParticipant }>(`/channels/${channelId}/challenges/${challengeId}/progress`, { progress }),

  getChallengeLeaderboard: (channelId: string, challengeId: string) =>
    api.get<ChannelChallengeLeaderboardResponse>(`/channels/${channelId}/challenges/${challengeId}/leaderboard`),

  // Spec 277 Track C: Channel Polls — same unwrapResponse() caveat as
  // Challenges above: typed here as the unwrapped shape, but the actual
  // wire response is always `{ success: true, data: {...} }`.
  listPolls: (channelId: string, params?: { limit?: number; offset?: number }) =>
    api.get<ChannelPollListResponse>(`/channels/${channelId}/polls`, { params }),

  getPoll: (channelId: string, pollId: string) =>
    api.get<{ success: boolean; data: ChannelPoll }>(`/channels/${channelId}/polls/${pollId}`),

  createPoll: (channelId: string, data: CreateChannelPollInput) =>
    api.post<{ success: boolean; data: ChannelPoll }>(`/channels/${channelId}/polls`, data),

  votePoll: (channelId: string, pollId: string, optionId: number) =>
    api.post<{ success: boolean; data: ChannelPoll }>(`/channels/${channelId}/polls/${pollId}/vote`, { optionId }),

  // Spec 275: Channel Events
  getEvents: (channelId: string, params?: { status?: 'upcoming' | 'past' | 'all'; page?: number; limit?: number }) =>
    api.get<ChannelEventListResponse>(`/channels/${channelId}/events`, { params }),

  createEvent: (channelId: string, data: {
    title: string;
    description?: string;
    eventType?: string;
    startTime: string;
    endTime?: string;
    timezone?: string;
    location?: string;
    isVirtual?: boolean;
    virtualLink?: string;
    maxAttendees?: number;
    coverImageUrl?: string;
  }) => api.post<{ data: ChannelEvent }>(`/channels/${channelId}/events`, data),

  getEvent: (channelId: string, eventId: string) =>
    api.get<{ data: ChannelEvent }>(`/channels/${channelId}/events/${eventId}`),

  updateEvent: (channelId: string, eventId: string, data: Partial<{
    title: string;
    description: string;
    startTime: string;
    endTime: string;
    location: string;
    isVirtual: boolean;
    virtualLink: string;
    maxAttendees: number;
    coverImageUrl: string;
  }>) => api.put<{ data: ChannelEvent }>(`/channels/${channelId}/events/${eventId}`, data),

  deleteEvent: (channelId: string, eventId: string) =>
    api.delete(`/channels/${channelId}/events/${eventId}`),

  cancelEvent: (channelId: string, eventId: string) =>
    api.post(`/channels/${channelId}/events/${eventId}/cancel`),

  rsvpEvent: (channelId: string, eventId: string, status: 'going' | 'maybe' | 'not_going') =>
    api.post<{ data: { status: string } }>(`/channels/${channelId}/events/${eventId}/rsvp`, { status }),

  removeRsvp: (channelId: string, eventId: string) =>
    api.delete(`/channels/${channelId}/events/${eventId}/rsvp`),

  // Spec 277 Track C: Channel Moderator Admin Toolkit
  // Post-scoped actions (hide/pin/lock) — moderator-only, enforced server-side.
  hidePost: (channelId: string, postId: string, reason?: string) =>
    api.post(`/channels/${channelId}/posts/${postId}/moderation/hide`, { reason }),

  unhidePost: (channelId: string, postId: string) =>
    api.post(`/channels/${channelId}/posts/${postId}/moderation/unhide`),

  pinPost: (channelId: string, postId: string) =>
    api.post(`/channels/${channelId}/posts/${postId}/moderation/pin`),

  unpinPost: (channelId: string, postId: string) =>
    api.post(`/channels/${channelId}/posts/${postId}/moderation/unpin`),

  lockPost: (channelId: string, postId: string) =>
    api.post(`/channels/${channelId}/posts/${postId}/moderation/lock`),

  unlockPost: (channelId: string, postId: string) =>
    api.post(`/channels/${channelId}/posts/${postId}/moderation/unlock`),

  // Member-scoped actions (warn/mute/ban) — moderator-only, always logged.
  warnMember: (channelId: string, userId: string, reason: string) =>
    api.post<{ success: boolean; data: ChannelModerationLogEntry }>(
      `/channels/${channelId}/members/${userId}/warn`, { reason }
    ),

  muteMember: (channelId: string, userId: string, reason: string, durationHours: number | null) =>
    api.post<{ success: boolean; data: ChannelModerationLogEntry }>(
      `/channels/${channelId}/members/${userId}/mute`, { reason, durationHours }
    ),

  unmuteMember: (channelId: string, userId: string) =>
    api.post<{ success: boolean; data: ChannelModerationLogEntry }>(`/channels/${channelId}/members/${userId}/unmute`),

  banMember: (channelId: string, userId: string, reason: string) =>
    api.post<{ success: boolean; data: ChannelModerationLogEntry }>(
      `/channels/${channelId}/members/${userId}/ban`, { reason }
    ),

  unbanMember: (channelId: string, userId: string) =>
    api.post<{ success: boolean; data: ChannelModerationLogEntry }>(`/channels/${channelId}/members/${userId}/unban`),

  // Moderation log — MODERATOR-ONLY full log (403 for non-moderators, no fallback).
  getModerationLog: (channelId: string, params?: { actionType?: ChannelModerationActionType; page?: number; limit?: number }) =>
    api.get<{ success: boolean; data: { entries: ChannelModerationLogEntry[]; total: number; page: number; limit: number } }>(
      `/channels/${channelId}/moderation-log`, { params }
    ),

  // Moderation log — CEO visibility split: any authenticated user, but only
  // ever returns the CALLER's own notices, never another member's.
  getMyModerationNotices: (channelId: string) =>
    api.get<{ success: boolean; data: { notices: ChannelModerationNotice[] } }>(
      `/channels/${channelId}/moderation-log/mine`
    ),

  // Spec 283: Channel Self-Identification Tags. Dark-launched behind the
  // channel_identity_tags feature flag. Every endpoint returns
  // { success: true, data: ... } — typed here as the unwrapped shape only,
  // same convention as the rest of this file (see listChallenges comment
  // above) — always pipe through unwrapResponse()/unwrapArray().

  // Member-facing
  getTagCategories: (channelId: string) =>
    api.get<{ success: boolean; data: EnabledChannelTagCategory[] }>(`/channels/${channelId}/tag-categories`),

  getMyTags: (channelId: string) =>
    api.get<{ success: boolean; data: OwnChannelTags }>(`/channels/${channelId}/tags/me`),

  // user_ids is REQUIRED backend-side (2026-07-27 follow-up) — a missing/
  // empty value 400s rather than returning every tagged member of the
  // channel. Callers must pass a non-empty, capped author-id list.
  getMemberTags: (channelId: string, userPublicIds: string[]) =>
    api.get<{ success: boolean; data: ChannelMemberTagValue[] }>(`/channels/${channelId}/tags/members`, {
      params: { user_ids: userPublicIds.join(',') },
    }),

  setTags: (channelId: string, catalogCategoryId: number, values: string[]) =>
    api.put<{ success: boolean; message: string }>(`/channels/${channelId}/tags/${catalogCategoryId}`, { values }),

  clearTags: (channelId: string, catalogCategoryId: number) =>
    api.delete<{ success: boolean; message: string }>(`/channels/${channelId}/tags/${catalogCategoryId}`),

  // Moderator-facing
  getAvailableTagCategories: (channelId: string) =>
    api.get<{ success: boolean; data: ChannelTagCategory[] }>(`/channels/${channelId}/tag-categories/available`),

  enableTagCategory: (channelId: string, catalogCategoryId: number) =>
    api.post<{ success: boolean; message: string }>(`/channels/${channelId}/tag-categories`, { catalogCategoryId }),

  disableTagCategory: (channelId: string, channelTagCategoryId: number) =>
    api.delete<{ success: boolean; message: string }>(`/channels/${channelId}/tag-categories/${channelTagCategoryId}`),

  requestTagCategory: (channelId: string, label: string, values: string[]) =>
    api.post<{ success: boolean; data: ChannelTagCategory }>(`/channels/${channelId}/tag-categories/request`, { label, values }),
};
